import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';
import { renderPdfToCanvas } from './pdfRender';

/**
 * FloorPlanCanvas — shared floor-plan render surface (Sprint 3 D3).
 *
 * Base layer is an <img> (image/svg) or a pdf.js-rendered <canvas> (pdf).
 * On top sits an SVG overlay with viewBox="0 0 1 1" + preserveAspectRatio="none"
 * so unit polygons stored as NORMALIZED 0..1 coords map 1:1 to the displayed box
 * at any size/zoom.
 *
 * Props:
 *   floorPlan        { url, format, width, height }
 *   units            [{ unit_id, tenant_name, status, polygon:[[0..1]], kpis }]
 *   mode             'view' | 'edit'
 *   activeUnitId     (edit) unit whose polygon is being drawn
 *   onSelectUnit(u)  view: drilldown · edit: set active unit
 *   onPolygonChange(unitId, polygon)  (edit) geometry mutated
 */

const clamp01 = (n) => Math.min(1, Math.max(0, n));

function unitColors(u) {
    if ((u?.kpis?.anomaly_count || 0) > 0) return { fill: '#c0392b', stroke: '#c0392b' };
    switch (u?.status) {
        case 'vacant': return { fill: '#94a3b8', stroke: '#64748b' };
        case 'under-fitout': return { fill: '#f59e0b', stroke: '#d97706' };
        case 'occupied':
        default: return { fill: '#2563eb', stroke: '#1d4ed8' };
    }
}

const pointsAttr = (polygon) => polygon.map((p) => `${p[0]},${p[1]}`).join(' ');

export default function FloorPlanCanvas({
    floorPlan,
    units = [],
    mode = 'view',
    activeUnitId = null,
    onSelectUnit,
    onPolygonChange,
}) {
    const { t } = useTranslation();
    const wrapRef = useRef(null);
    const svgRef = useRef(null);
    const canvasRef = useRef(null);
    const [pdfError, setPdfError] = useState(null);
    const [hover, setHover] = useState(null); // { unit, x, y }
    const [drag, setDrag] = useState(null);   // { unitId, index }

    const url = floorPlan?.url || null;
    const format = (floorPlan?.format || '').toLowerCase();
    const isPdf = format === 'pdf' || /\.pdf($|\?)/i.test(url || '');

    // ─── PDF base layer ──────────────────────────────────────────
    useEffect(() => {
        if (!url || !isPdf || !canvasRef.current) return;
        let cancelled = false;
        setPdfError(null);
        const target = Math.max(wrapRef.current?.clientWidth || 0, 1200);
        renderPdfToCanvas(url, canvasRef.current, target).catch((e) => {
            if (!cancelled) setPdfError(e.message || t('Failed to render PDF'));
        });
        return () => { cancelled = true; };
    }, [url, isPdf, t]);

    const eventToNorm = useCallback((evt) => {
        const rect = svgRef.current.getBoundingClientRect();
        return [
            clamp01((evt.clientX - rect.left) / rect.width),
            clamp01((evt.clientY - rect.top) / rect.height),
        ];
    }, []);

    // ─── Vertex dragging (edit) ──────────────────────────────────
    useEffect(() => {
        if (!drag) return;
        const move = (evt) => {
            const u = units.find((x) => x.unit_id === drag.unitId);
            if (!u) return;
            const next = u.polygon.map((p, i) => (i === drag.index ? eventToNorm(evt) : p));
            onPolygonChange && onPolygonChange(drag.unitId, next);
        };
        const up = () => setDrag(null);
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        return () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
    }, [drag, units, eventToNorm, onPolygonChange]);

    const handleSurfaceClick = (evt) => {
        if (mode !== 'edit' || !activeUnitId || drag) return;
        const active = units.find((u) => u.unit_id === activeUnitId);
        if (!active) return;
        onPolygonChange && onPolygonChange(activeUnitId, [...(active.polygon || []), eventToNorm(evt)]);
    };

    if (!url) {
        return (
            <div className="v3-text-muted" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '48px 0', justifyContent: 'center' }}>
                <ImageOff size={18} />
                {t('No floor plan uploaded for this property yet.')}
            </div>
        );
    }

    return (
        <div ref={wrapRef} style={{ position: 'relative', width: '100%', lineHeight: 0 }}>
            {/* Base layer (pointer-transparent so the SVG receives interaction) */}
            {isPdf ? (
                <canvas ref={canvasRef} style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6, background: '#fff', pointerEvents: 'none' }} />
            ) : (
                <img src={url} alt={t('floor plan')} style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6, pointerEvents: 'none' }} />
            )}
            {pdfError && (
                <div className="v3-text-danger" style={{ position: 'absolute', top: 8, left: 8, fontSize: 12 }}>{t('PDF')}: {pdfError}</div>
            )}

            {/* Normalized polygon overlay */}
            <svg
                ref={svgRef}
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                onClick={handleSurfaceClick}
                style={{
                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                    cursor: mode === 'edit' && activeUnitId ? 'crosshair' : 'default',
                }}
            >
                {units.map((u) => {
                    if (!u.polygon || u.polygon.length < 2) return null;
                    const c = unitColors(u);
                    const isActive = mode === 'edit' && u.unit_id === activeUnitId;
                    return (
                        <polygon
                            key={u.unit_id}
                            points={pointsAttr(u.polygon)}
                            fill={c.fill}
                            fillOpacity={isActive ? 0.45 : (mode === 'edit' ? 0.18 : 0.32)}
                            stroke={isActive ? '#0ea5e9' : c.stroke}
                            strokeWidth={isActive ? 2.5 : 1.5}
                            vectorEffect="non-scaling-stroke"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); onSelectUnit && onSelectUnit(u); }}
                            onMouseEnter={(e) => mode === 'view' && setHover({ unit: u, x: e.clientX, y: e.clientY })}
                            onMouseMove={(e) => mode === 'view' && setHover({ unit: u, x: e.clientX, y: e.clientY })}
                            onMouseLeave={() => setHover(null)}
                        />
                    );
                })}

            </svg>

            {/* Editable vertex handles (HTML, pixel-sized — positioned by % so they
                stay aligned with the normalized overlay at any display size). */}
            {mode === 'edit' && activeUnitId && (() => {
                const active = units.find((u) => u.unit_id === activeUnitId);
                if (!active || !active.polygon) return null;
                return active.polygon.map((p, i) => (
                    <div
                        key={i}
                        onPointerDown={(e) => { e.stopPropagation(); setDrag({ unitId: activeUnitId, index: i }); }}
                        title={t('Drag to move · right-click to remove')}
                        onContextMenu={(e) => {
                            e.preventDefault();
                            const next = active.polygon.filter((_, idx) => idx !== i);
                            onPolygonChange && onPolygonChange(activeUnitId, next);
                        }}
                        style={{
                            position: 'absolute', left: `${p[0] * 100}%`, top: `${p[1] * 100}%`,
                            width: 12, height: 12, marginLeft: -6, marginTop: -6,
                            borderRadius: '50%', background: '#0ea5e9', border: '2px solid #fff',
                            boxShadow: '0 1px 3px rgba(0,0,0,.4)', cursor: 'grab', touchAction: 'none',
                        }}
                    />
                ));
            })()}

            {/* Hover tooltip (view) */}
            {hover && (
                <div style={{
                    position: 'fixed', left: hover.x + 12, top: hover.y + 12, zIndex: 50,
                    background: 'var(--v3-glass-bg, #0f172a)', color: '#fff', padding: '6px 10px',
                    borderRadius: 6, fontSize: 12, pointerEvents: 'none', boxShadow: '0 4px 12px rgba(0,0,0,.3)',
                }}>
                    <div style={{ fontWeight: 600 }}>{hover.unit.unit_id} · {hover.unit.tenant_name || '—'}</div>
                    <div style={{ opacity: 0.85 }}>
                        {t('OCR')} {hover.unit.kpis?.ocr_pct != null ? `${Number(hover.unit.kpis.ocr_pct).toFixed(1)}%` : '—'}
                        {' · '}{t('Anomalies')} {hover.unit.kpis?.anomaly_count || 0}
                        {' · '}{hover.unit.status || '—'}
                    </div>
                </div>
            )}
        </div>
    );
}
